import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubmissions } from '../hooks/useSubmissions';
import { useTools } from '../hooks/useTools';
import { supabase } from '../lib/supabase';
import { GlassCard } from '../components/ui/GlassCard';
import { Check, X, ExternalLink, RefreshCw, LogOut, Plus, Trash2, Star, Edit, Save, XCircle, Image as ImageIcon, Video, Music, Code, PenTool, Palette, Zap, Search, Sparkles, Globe, Shield, MessageSquare } from 'lucide-react';
import { AdminAddToolModal } from '../components/AdminAddToolModal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Tool, PricingTier, ToolReview } from '../types/database';
const CATEGORIES = [{
  name: 'Image',
  icon: ImageIcon
}, {
  name: 'Video',
  icon: Video
}, {
  name: 'Audio',
  icon: Music
}, {
  name: 'Development',
  icon: Code
}, {
  name: 'Writing',
  icon: PenTool
}, {
  name: 'Design',
  icon: Palette
}, {
  name: 'Productivity',
  icon: Zap
}, {
  name: 'Research',
  icon: Search
}, {
  name: 'Other',
  icon: Sparkles
}];
export function AdminDashboardPage() {
  const navigate = useNavigate();
  const {
    submissions,
    loading: submissionsLoading,
    approveSubmission,
    rejectSubmission,
    refresh: refreshSubmissions
  } = useSubmissions();
  const {
    tools,
    loading: toolsLoading,
    refresh: refreshTools
  } = useTools();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'submissions' | 'tools' | 'reviews'>('submissions');
  const [editingToolId, setEditingToolId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  // Reviews state
  const [reviews, setReviews] = useState<ToolReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  useEffect(() => {
    checkAuth();
    fetchReviews();
  }, []);
  const checkAuth = async () => {
    const {
      data: {
        session
      }
    } = await supabase.auth.getSession();
    if (!session) {
      navigate('/admin');
    } else {
      setUserEmail(session.user.email || '');
    }
  };
  const fetchReviews = async () => {
    try {
      setReviewsLoading(true);
      const {
        data,
        error
      } = await supabase.from('tool_reviews').select('*').order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };
  const handleApprove = async (submission: any) => {
    setProcessingId(submission.id);
    await approveSubmission(submission);
    setProcessingId(null);
    refreshTools();
  };
  const handleReject = async (id: string) => {
    setProcessingId(id);
    await rejectSubmission(id);
    setProcessingId(null);
  };
  const startEditing = (tool: Tool) => {
    setEditingToolId(tool.id);
    setEditForm({
      name: tool.name,
      description: tool.description,
      website_url: tool.website_url,
      category: tool.category,
      logo_url: tool.logo_url,
      pricing_tiers: tool.pricing_tiers || []
    });
  };
  const cancelEditing = () => {
    setEditingToolId(null);
    setEditForm(null);
  };
  const saveEdit = async (toolId: string) => {
    try {
      const {
        error
      } = await supabase.from('tools').update({
        name: editForm.name,
        description: editForm.description,
        logo_url: editForm.logo_url,
        website_url: editForm.website_url,
        category: editForm.category,
        pricing_tiers: editForm.pricing_tiers
      }).eq('id', toolId);
      if (error) {
        console.error('Supabase error:', error);
        alert(`Failed to update tool: ${error.message}`);
        return;
      }
      setEditingToolId(null);
      setEditForm(null);
      refreshTools();
    } catch (err: any) {
      console.error('Error updating tool:', err);
      alert(`Failed to update tool: ${err.message}`);
    }
  };
  const handleDeleteTool = (tool: Tool) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Tool?',
      message: `Are you sure you want to delete "${tool.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const {
            error
          } = await supabase.from('tools').delete().eq('id', tool.id);
          if (error) {
            console.error('Supabase error:', error);
            alert(`Failed to delete tool: ${error.message}`);
            return;
          }
          refreshTools();
        } catch (err: any) {
          console.error('Error deleting tool:', err);
          alert(`Failed to delete tool: ${err.message}`);
        }
      }
    });
  };
  const handleToggleFeatured = (tool: Tool) => {
    const action = tool.is_featured ? 'unfeature' : 'feature';
    setConfirmDialog({
      isOpen: true,
      title: `${action === 'feature' ? 'Feature' : 'Unfeature'} Tool?`,
      message: `${action === 'feature' ? 'This tool will appear in "Most Popular Tools".' : 'This tool will be removed from "Most Popular Tools".'}`,
      onConfirm: async () => {
        try {
          const {
            error
          } = await supabase.from('tools').update({
            is_featured: !tool.is_featured
          }).eq('id', tool.id);
          if (error) {
            console.error('Supabase error:', error);
            alert(`Failed to update featured status: ${error.message}`);
            return;
          }
          refreshTools();
        } catch (err: any) {
          console.error('Error toggling featured status:', err);
          alert(`Failed to update featured status: ${err.message}`);
        }
      }
    });
  };
  const handleRefresh = () => {
    refreshSubmissions();
    refreshTools();
    fetchReviews();
  };
  const updateEditForm = (field: string, value: any) => {
    setEditForm({
      ...editForm,
      [field]: value
    });
  };
  const addPricingTier = () => {
    setEditForm({
      ...editForm,
      pricing_tiers: [...editForm.pricing_tiers, {
        name: '',
        price: '',
        features: []
      }]
    });
  };
  const removePricingTier = (index: number) => {
    setEditForm({
      ...editForm,
      pricing_tiers: editForm.pricing_tiers.filter((_: any, i: number) => i !== index)
    });
  };
  const updatePricingTier = (index: number, field: string, value: any) => {
    const newTiers = [...editForm.pricing_tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: value
    };
    setEditForm({
      ...editForm,
      pricing_tiers: newTiers
    });
  };
  const handleApproveReview = async (reviewId: string) => {
    try {
      console.log('🔄 Attempting to approve review:', reviewId);
      console.log('👤 Current user:', userEmail);
      const {
        data,
        error
      } = await supabase.from('tool_reviews').update({
        status: 'approved'
      }).eq('id', reviewId).select();
      console.log('📊 Supabase response:', {
        data,
        error
      });
      if (error) {
        console.error('❌ Supabase error:', error);
        alert(`Failed to approve review: ${error.message}\n\nDetails: ${JSON.stringify(error, null, 2)}`);
        throw error;
      }
      console.log('✅ Review approved successfully');
      fetchReviews();
    } catch (error: any) {
      console.error('❌ Error approving review:', error);
      alert(`Failed to approve review: ${error.message || 'Unknown error'}`);
    }
  };
  const handleRejectReview = async (reviewId: string) => {
    try {
      console.log('🔄 Attempting to reject review:', reviewId);
      const {
        data,
        error
      } = await supabase.from('tool_reviews').update({
        status: 'rejected'
      }).eq('id', reviewId).select();
      console.log('📊 Supabase response:', {
        data,
        error
      });
      if (error) {
        console.error('❌ Supabase error:', error);
        alert(`Failed to reject review: ${error.message}\n\nDetails: ${JSON.stringify(error, null, 2)}`);
        throw error;
      }
      console.log('✅ Review rejected successfully');
      fetchReviews();
    } catch (error: any) {
      console.error('❌ Error rejecting review:', error);
      alert(`Failed to reject review: ${error.message || 'Unknown error'}`);
    }
  };
  const handleDeleteReview = async (reviewId: string) => {
    if (!confirm('Are you sure you want to permanently delete this review? This action cannot be undone.')) {
      return;
    }
    try {
      console.log('🗑️ Attempting to delete review:', reviewId);
      const {
        error
      } = await supabase.from('tool_reviews').delete().eq('id', reviewId);
      if (error) {
        console.error('❌ Supabase error:', error);
        alert(`Failed to delete review: ${error.message}`);
        throw error;
      }
      console.log('✅ Review deleted successfully');
      fetchReviews();
    } catch (error: any) {
      console.error('❌ Error deleting review:', error);
      alert(`Failed to delete review: ${error.message || 'Unknown error'}`);
    }
  };
  const getAverageRating = (review: ToolReview) => {
    return ((review.rating_ease_of_use + review.rating_pricing + review.rating_utility) / 3).toFixed(1);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const SelectedCategoryIcon = editForm ? CATEGORIES.find(c => c.name === editForm.category)?.icon || Sparkles : Sparkles;
  // Separate tools by source
  const adminTools = tools.filter(t => (t as any).source === 'admin');
  const scrapedTools = tools.filter(t => (t as any).source === 'scraped');
  return <div className="min-h-screen bg-black text-white p-8" data-id="element-743">
      <div className="max-w-7xl mx-auto" data-id="element-744">
        <div className="flex items-center justify-between mb-8" data-id="element-745">
          <div data-id="element-746">
            <h1 className="text-3xl font-bold font-special" data-id="element-747">Admin Dashboard</h1>
            {userEmail && <p className="text-sm text-gray-400 mt-1" data-id="element-748">
                Logged in as: {userEmail}
              </p>}
          </div>
          <div className="flex items-center gap-3" data-id="element-749">
            <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors border border-white/10" data-id="element-750">
              <ExternalLink size={16} data-id="element-751" />
              View Site
            </a>
            <button onClick={() => setIsAddToolModalOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold transition-colors" data-id="element-752">
              <Plus size={16} data-id="element-753" />
              Add Tool
            </button>
            <button onClick={handleRefresh} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" title="Refresh data" data-id="element-754">
              <RefreshCw size={20} data-id="element-755" />
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors" data-id="element-756">
              <LogOut size={16} data-id="element-757" />
              Logout
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-white/10" data-id="element-758">
          <button onClick={() => setActiveTab('submissions')} className={`px-6 py-3 font-medium transition-all relative ${activeTab === 'submissions' ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`} data-id="element-759">
            Pending Submissions ({submissions.length})
            {activeTab === 'submissions' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" data-id="element-760" />}
          </button>
          <button onClick={() => setActiveTab('reviews')} className={`px-6 py-3 font-medium transition-all relative ${activeTab === 'reviews' ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`} data-id="element-761">
            Reviews ({reviews.filter(r => r.status === 'pending').length}{' '}
            pending)
            {activeTab === 'reviews' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" data-id="element-762" />}
          </button>
          <button onClick={() => setActiveTab('tools')} className={`px-6 py-3 font-medium transition-all relative ${activeTab === 'tools' ? 'text-cyan-400' : 'text-gray-400 hover:text-white'}`} data-id="element-763">
            All Tools ({tools.length})
            {activeTab === 'tools' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" data-id="element-764" />}
          </button>
        </div>

        {/* Pending Submissions Tab */}
        {activeTab === 'submissions' && <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-id="element-765">
            <div className="p-6 border-b border-white/10" data-id="element-766">
              <h2 className="text-xl font-bold" data-id="element-767">
                Pending Submissions ({submissions.length})
              </h2>
            </div>

            {submissionsLoading ? <div className="p-8 text-center text-gray-400" data-id="element-768">
                Loading submissions...
              </div> : submissions.length === 0 ? <div className="p-8 text-center text-gray-400" data-id="element-769">
                No pending submissions
              </div> : <div className="divide-y divide-white/10" data-id="element-770">
                {submissions.map(submission => <div key={submission.id} className="p-6 flex flex-col md:flex-row gap-6" data-id="element-771">
                    <div className="flex-1" data-id="element-772">
                      <div className="flex items-start gap-4 mb-4" data-id="element-773">
                        {submission.logo_url && <img src={submission.logo_url} alt={submission.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-774" />}
                        <div data-id="element-775">
                          <h3 className="text-lg font-bold text-white" data-id="element-776">
                            {submission.name}
                          </h3>
                          <a href={submission.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-777">
                            {submission.website_url} <ExternalLink size={12} data-id="element-778" />
                          </a>
                        </div>
                      </div>

                      <p className="text-gray-300 mb-4" data-id="element-779">
                        {submission.description}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-4" data-id="element-780">
                        <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-781">
                          {submission.category}
                        </span>
                        {submission.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-782">
                              {tier.name}: {tier.price}
                            </span>)}
                      </div>

                      <div className="text-xs text-gray-500" data-id="element-783">
                        Submitted by: {submission.submitter_email}
                      </div>
                    </div>

                    <div className="flex flex-row md:flex-col gap-2 justify-center" data-id="element-784">
                      <button onClick={() => handleApprove(submission)} disabled={!!processingId} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors disabled:opacity-50" data-id="element-785">
                        <Check size={16} data-id="element-786" /> Approve
                      </button>
                      <button onClick={() => handleReject(submission.id)} disabled={!!processingId} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors disabled:opacity-50" data-id="element-787">
                        <X size={16} data-id="element-788" /> Reject
                      </button>
                    </div>
                  </div>)}
              </div>}
          </div>}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && <div className="space-y-6" data-id="element-789">
            {/* Pending Reviews */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-id="element-790">
              <div className="p-6 border-b border-white/10" data-id="element-791">
                <h2 className="text-xl font-bold flex items-center gap-2" data-id="element-792">
                  <MessageSquare size={20} className="text-yellow-400" data-id="element-793" />
                  Pending Reviews (
                  {reviews.filter(r => r.status === 'pending').length})
                </h2>
              </div>

              {reviewsLoading ? <div className="p-8 text-center text-gray-400" data-id="element-794">
                  Loading reviews...
                </div> : reviews.filter(r => r.status === 'pending').length === 0 ? <div className="p-8 text-center text-gray-400" data-id="element-795">
                  No pending reviews
                </div> : <div className="divide-y divide-white/10" data-id="element-796">
                  {reviews.filter(r => r.status === 'pending').map(review => <div key={review.id} className="p-6" data-id="element-797">
                        <div className="flex items-start justify-between mb-4" data-id="element-798">
                          <div data-id="element-799">
                            <div className="flex items-center gap-2 mb-1" data-id="element-800">
                              <span className="font-bold text-white" data-id="element-801">
                                {review.user_email}
                              </span>
                              <span className="text-xs text-gray-500" data-id="element-802">
                                reviewed
                              </span>
                              <span className="text-cyan-400 font-medium" data-id="element-803">
                                {review.tool_id}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500" data-id="element-804">
                              {formatDate(review.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1" data-id="element-805">
                            {[1, 2, 3, 4, 5].map(star => <Star key={star} size={14} className={star <= Math.round(parseFloat(getAverageRating(review))) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'} data-id="element-806" />)}
                            <span className="ml-2 text-sm font-bold text-white" data-id="element-807">
                              {getAverageRating(review)}
                            </span>
                          </div>
                        </div>

                        {/* Individual Ratings */}
                        <div className="grid grid-cols-3 gap-3 mb-4" data-id="element-808">
                          <div className="text-center p-3 rounded-lg bg-white/5" data-id="element-809">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-810">
                              Ease of Use
                            </div>
                            <div className="text-lg font-bold text-white" data-id="element-811">
                              {review.rating_ease_of_use}/5
                            </div>
                          </div>
                          <div className="text-center p-3 rounded-lg bg-white/5" data-id="element-812">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-813">
                              Pricing
                            </div>
                            <div className="text-lg font-bold text-white" data-id="element-814">
                              {review.rating_pricing}/5
                            </div>
                          </div>
                          <div className="text-center p-3 rounded-lg bg-white/5" data-id="element-815">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-816">
                              Utility
                            </div>
                            <div className="text-lg font-bold text-white" data-id="element-817">
                              {review.rating_utility}/5
                            </div>
                          </div>
                        </div>

                        {/* Review Text */}
                        <div className="p-4 rounded-lg bg-black/20 border border-white/10 mb-4" data-id="element-818">
                          <p className="text-gray-300 leading-relaxed" data-id="element-819">
                            {review.review_text}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2" data-id="element-820">
                          <button onClick={() => handleApproveReview(review.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors" data-id="element-821">
                            <Check size={16} data-id="element-822" /> Approve
                          </button>
                          <button onClick={() => handleRejectReview(review.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors" data-id="element-823">
                            <X size={16} data-id="element-824" /> Reject
                          </button>
                          <button onClick={() => handleDeleteReview(review.id)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-500/20 text-gray-400 hover:bg-gray-500/30 transition-colors" title="Permanently delete this review" data-id="element-825">
                            <Trash2 size={16} data-id="element-826" /> Delete
                          </button>
                        </div>
                      </div>)}
                </div>}
            </div>

            {/* Approved Reviews */}
            {reviews.filter(r => r.status === 'approved').length > 0 && <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden" data-id="element-827">
                <div className="p-6 border-b border-white/10" data-id="element-828">
                  <h2 className="text-xl font-bold flex items-center gap-2" data-id="element-829">
                    <Check size={20} className="text-green-400" data-id="element-830" />
                    Approved Reviews (
                    {reviews.filter(r => r.status === 'approved').length})
                  </h2>
                </div>
                <div className="divide-y divide-white/10 max-h-96 overflow-y-auto" data-id="element-831">
                  {reviews.filter(r => r.status === 'approved').map(review => <div key={review.id} className="p-4 opacity-75" data-id="element-832">
                        <div className="flex items-center justify-between" data-id="element-833">
                          <div className="flex items-center gap-2" data-id="element-834">
                            <span className="text-sm font-medium text-white" data-id="element-835">
                              {review.user_email}
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-836">→</span>
                            <span className="text-sm text-cyan-400" data-id="element-837">
                              {review.tool_id}
                            </span>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-838">
                            <div className="flex items-center gap-2" data-id="element-839">
                              <span className="text-sm font-bold text-white" data-id="element-840">
                                {getAverageRating(review)}
                              </span>
                              <Star size={14} className="fill-yellow-400 text-yellow-400" data-id="element-841" />
                            </div>
                            <button onClick={() => handleDeleteReview(review.id)} className="p-1.5 rounded-lg bg-gray-500/20 text-gray-400 hover:bg-red-500/30 hover:text-red-400 transition-colors" title="Delete review" data-id="element-842">
                              <Trash2 size={14} data-id="element-843" />
                            </button>
                          </div>
                        </div>
                      </div>)}
                </div>
              </div>}
          </div>}

        {/* All Tools Tab */}
        {activeTab === 'tools' && <div className="space-y-8" data-id="element-844">
            {toolsLoading ? <div className="p-8 text-center text-gray-400 bg-white/5 rounded-xl border border-white/10" data-id="element-845">
                Loading tools...
              </div> : tools.length === 0 ? <div className="p-8 text-center text-gray-400 bg-white/5 rounded-xl border border-white/10" data-id="element-846">
                No tools yet. Click "Add Tool" to get started!
              </div> : <>
                {/* Admin Tools Section */}
                {adminTools.length > 0 && <div data-id="element-847">
                    <div className="flex items-center gap-3 mb-4" data-id="element-848">
                      <Shield size={20} className="text-purple-400" data-id="element-849" />
                      <h2 className="text-xl font-bold text-white" data-id="element-850">
                        Curated Tools ({adminTools.length})
                      </h2>
                      <span className="text-sm text-gray-500" data-id="element-851">
                        — Uploaded via Admin
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-852">
                      {adminTools.map(tool => <div key={tool.id} className="bg-white/5 rounded-xl border border-white/10 p-6" data-id="element-853">
                          {editingToolId === tool.id ?
                // EDIT MODE
                <div className="space-y-4" data-id="element-854">
                              <div className="flex items-center justify-between mb-4" data-id="element-855">
                                <h3 className="text-lg font-bold text-white" data-id="element-856">
                                  Editing Tool
                                </h3>
                                <div className="flex gap-2" data-id="element-857">
                                  <button onClick={() => saveEdit(tool.id)} className="p-2 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30 transition-colors" title="Save changes" data-id="element-858">
                                    <Save size={18} data-id="element-859" />
                                  </button>
                                  <button onClick={cancelEditing} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 transition-colors" title="Cancel" data-id="element-860">
                                    <XCircle size={18} data-id="element-861" />
                                  </button>
                                </div>
                              </div>

                              {/* Edit form fields... (keeping existing edit form) */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-862">
                                <div data-id="element-863">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-864">
                                    Tool Name
                                  </label>
                                  <input type="text" value={editForm.name} onChange={e => updateEditForm('name', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-865" />
                                </div>
                                <div data-id="element-866">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-867">
                                    Website URL
                                  </label>
                                  <input type="url" value={editForm.website_url} onChange={e => updateEditForm('website_url', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-868" />
                                </div>
                              </div>

                              <div data-id="element-869">
                                <label className="block text-xs text-gray-400 mb-1" data-id="element-870">
                                  Description
                                </label>
                                <textarea value={editForm.description} onChange={e => updateEditForm('description', e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none resize-none" data-id="element-871" />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-872">
                                <div data-id="element-873">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-874">
                                    Category
                                  </label>
                                  <div className="relative" data-id="element-875">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-cyan-400 pointer-events-none" data-id="element-876">
                                      <SelectedCategoryIcon size={16} data-id="element-877" />
                                    </div>
                                    <select value={editForm.category} onChange={e => updateEditForm('category', e.target.value)} className="w-full pl-10 pr-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none appearance-none cursor-pointer" data-id="element-878">
                                      {CATEGORIES.map(cat => <option key={cat.name} value={cat.name} className="bg-gray-900" data-id="element-879">
                                          {cat.name}
                                        </option>)}
                                    </select>
                                  </div>
                                </div>
                                <div data-id="element-880">
                                  <label className="block text-xs text-gray-400 mb-1" data-id="element-881">
                                    Logo URL
                                  </label>
                                  <input type="url" value={editForm.logo_url} onChange={e => updateEditForm('logo_url', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-882" />
                                </div>
                              </div>

                              <div data-id="element-883">
                                <div className="flex items-center justify-between mb-2" data-id="element-884">
                                  <label className="block text-xs text-gray-400" data-id="element-885">
                                    Pricing Tiers
                                  </label>
                                  <button onClick={addPricingTier} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1" data-id="element-886">
                                    <Plus size={14} data-id="element-887" /> Add Tier
                                  </button>
                                </div>
                                <div className="space-y-2" data-id="element-888">
                                  {editForm.pricing_tiers.map((tier: any, index: number) => <div key={index} className="flex gap-2 items-center" data-id="element-889">
                                        <input type="text" value={tier.name} onChange={e => updatePricingTier(index, 'name', e.target.value)} placeholder="Plan name" className="flex-1 px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-890" />
                                        <input type="text" value={tier.price} onChange={e => updatePricingTier(index, 'price', e.target.value)} placeholder="Price" className="flex-1 px-3 py-2 rounded-lg bg-black/20 border border-white/10 text-white text-sm focus:border-cyan-500/50 focus:outline-none" data-id="element-891" />
                                        {editForm.pricing_tiers.length > 1 && <button onClick={() => removePricingTier(index)} className="p-2 text-gray-500 hover:text-red-400" data-id="element-892">
                                            <Trash2 size={16} data-id="element-893" />
                                          </button>}
                                      </div>)}
                                </div>
                              </div>
                            </div> :
                // VIEW MODE
                <div className="flex flex-col md:flex-row gap-6" data-id="element-894">
                              <div className="flex-1" data-id="element-895">
                                <div className="flex items-start gap-4 mb-4" data-id="element-896">
                                  {tool.logo_url && <img src={tool.logo_url} alt={tool.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-897" />}
                                  <div data-id="element-898">
                                    <div className="flex items-center gap-2" data-id="element-899">
                                      <h3 className="text-lg font-bold text-white" data-id="element-900">
                                        {tool.name}
                                      </h3>
                                      {tool.is_featured && <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-xs font-medium flex items-center gap-1" data-id="element-901">
                                          <Star size={12} fill="currentColor" data-id="element-902" />
                                          Featured
                                        </span>}
                                    </div>
                                    <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-903">
                                      {tool.website_url}{' '}
                                      <ExternalLink size={12} data-id="element-904" />
                                    </a>
                                  </div>
                                </div>

                                <p className="text-gray-300 mb-4" data-id="element-905">
                                  {tool.description}
                                </p>

                                <div className="flex flex-wrap gap-2" data-id="element-906">
                                  <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-907">
                                    {tool.category}
                                  </span>
                                  {tool.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-908">
                                        {tier.name}: {tier.price}
                                      </span>)}
                                </div>
                              </div>

                              <div className="flex flex-row md:flex-col gap-2 justify-center" data-id="element-909">
                                <button onClick={() => startEditing(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-cyan-400 transition-colors" title="Edit" data-id="element-910">
                                  <Edit size={18} data-id="element-911" />
                                </button>
                                <button onClick={() => handleToggleFeatured(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-yellow-400 transition-colors" title={tool.is_featured ? 'Unfeature' : 'Feature'} data-id="element-912">
                                  <Star size={18} fill={tool.is_featured ? 'currentColor' : 'none'} data-id="element-913" />
                                </button>
                                <button onClick={() => handleDeleteTool(tool)} className="p-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 hover:text-red-400 transition-colors" title="Delete" data-id="element-914">
                                  <Trash2 size={18} data-id="element-915" />
                                </button>
                              </div>
                            </div>}
                        </div>)}
                    </div>
                  </div>}

                {/* Scraped Tools Section */}
                {scrapedTools.length > 0 && <div data-id="element-916">
                    <div className="flex items-center gap-3 mb-4" data-id="element-917">
                      <Globe size={20} className="text-cyan-400" data-id="element-918" />
                      <h2 className="text-xl font-bold text-white" data-id="element-919">
                        Scraped Tools ({scrapedTools.length})
                      </h2>
                      <span className="text-sm text-gray-500" data-id="element-920">
                        — Live data from web scraping (read-only)
                      </span>
                    </div>
                    <div className="space-y-4" data-id="element-921">
                      {scrapedTools.map(tool => <div key={tool.id} className="bg-white/5 rounded-xl border border-white/10 p-6 opacity-75" data-id="element-922">
                          <div className="flex flex-col md:flex-row gap-6" data-id="element-923">
                            <div className="flex-1" data-id="element-924">
                              <div className="flex items-start gap-4 mb-4" data-id="element-925">
                                {tool.logo_url && <img src={tool.logo_url} alt={tool.name} className="w-12 h-12 rounded-lg object-cover bg-white/10" data-id="element-926" />}
                                <div data-id="element-927">
                                  <h3 className="text-lg font-bold text-white" data-id="element-928">
                                    {tool.name}
                                  </h3>
                                  <a href={tool.website_url} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-sm flex items-center gap-1 hover:underline" data-id="element-929">
                                    {tool.website_url}{' '}
                                    <ExternalLink size={12} data-id="element-930" />
                                  </a>
                                </div>
                              </div>

                              <p className="text-gray-300 mb-4" data-id="element-931">
                                {tool.description}
                              </p>

                              <div className="flex flex-wrap gap-2" data-id="element-932">
                                <span className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-933">
                                  {tool.category}
                                </span>
                                {tool.pricing_tiers?.map((tier: any, i: number) => <span key={i} className="px-2 py-1 rounded bg-white/10 text-xs text-gray-300" data-id="element-934">
                                      {tier.name}: {tier.price}
                                    </span>)}
                              </div>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}
              </>}
          </div>}
      </div>

      <AdminAddToolModal isOpen={isAddToolModalOpen} onClose={() => {
      setIsAddToolModalOpen(false);
      refreshTools();
    }} data-id="element-935" />

      <ConfirmDialog isOpen={confirmDialog.isOpen} onClose={() => setConfirmDialog({
      ...confirmDialog,
      isOpen: false
    })} onConfirm={confirmDialog.onConfirm} title={confirmDialog.title} message={confirmDialog.message} confirmText="Confirm" cancelText="Cancel" variant="danger" data-id="element-936" />
    </div>;
}